<template>
  <div class="unconfirmed-item">
    <div class="unconfirmed-item__content">
      <div class="unconfirmed-item__heading">
        <h2>{{ title }}</h2>
        <p>
          {{ description }}
        </p>
      </div>

      <div class="unconfirmed-item__actions">
        <div class="unconfirmed-item__btns">
          <button
            class="unconfirmed-item__accept"
            @click="onConfirmReservation"
          >
            {{ $t("confirm") }}
          </button>
          <VDialog
            width="500"
            v-model="dialog"
            eager
            @update:model-value="(e) => onChangeEmployeeDialog(e)"
          >
            <template #activator="{ props }">
              <button class="unconfirmed-item__change" v-bind="props">
                {{
                  reservation.employees.length
                    ? $t("change_employee")
                    : $t("choose_employee")
                }}
              </button>
            </template>
            <VCard>
              <VCardTitle>
                <span class="text-h5">{{
                  reservation.employees.length
                    ? $t("change_employee")
                    : $t("choose_employee")
                }}</span>
              </VCardTitle>
              <VCardText>
                <v-container>
                  <VRow>
                    <VCol cols="12">
                      <VSelect
                        :error="!!employeeErrorMessage.length"
                        :error-messages="employeeErrorMessage"
                        :loading="employeeLoading"
                        :items="companyStore.employees"
                        item-title="name"
                        item-value="id"
                        label="Працівник"
                        multiple
                        required
                        v-model="selectedEmployee"
                      >
                        <template #no-data>
                          <p>{{ $t("employees_not_found") }}</p>
                        </template>
                      </VSelect>
                    </VCol>
                  </VRow>
                </v-container>
              </VCardText>
              <v-card-actions>
                <v-spacer></v-spacer>
                <VBtn
                  color="blue-darken-1"
                  variant="text"
                  @click="closeDialog(false)"
                >
                  {{ $t("cancel") }}
                </VBtn>
                <VBtn
                  color="blue-darken-1"
                  variant="text"
                  @click="closeDialog(true)"
                >
                  {{ $t("save") }}
                </VBtn>
              </v-card-actions>
            </VCard>
          </VDialog>
          <VDialog
            width="500"
            v-model="proposeDialog"
            eager
            @update:model-value="(e) => onProposeTimeDialog(e)"
          >
            <template #activator="{ props }">
              <button class="unconfirmed-item__propose" v-bind="props">
                {{ $t("propose_new_time") }}
              </button>
            </template>
            <VCard>
              <VCardTitle>
                <span class="text-h5">{{ $t("propose_new_time") }}</span>
              </VCardTitle>
              <VCardText>
                <p class="unconfirmed-item__propose-hint">
                  {{ $t("propose_new_time_hint") }}
                </p>
                <v-container>
                  <VRow>
                    <VCol cols="12">
                      <v-text-field
                        v-model="proposedStartDate"
                        :label="$t('start_date')"
                        type="datetime-local"
                      ></v-text-field>
                    </VCol>
                    <VCol cols="12">
                      <v-text-field
                        v-model="proposedEndDate"
                        :label="$t('end_date')"
                        type="datetime-local"
                        :error="!!proposeErrorMessage.length"
                        :error-messages="proposeErrorMessage"
                      ></v-text-field>
                    </VCol>
                  </VRow>
                </v-container>
              </VCardText>
              <v-card-actions>
                <v-spacer></v-spacer>
                <VBtn
                  color="blue-darken-1"
                  variant="text"
                  @click="closeProposeDialog(false)"
                >
                  {{ $t("cancel") }}
                </VBtn>
                <VBtn
                  color="blue-darken-1"
                  variant="text"
                  :loading="proposeLoading"
                  @click="closeProposeDialog(true)"
                >
                  {{ $t("save") }}
                </VBtn>
              </v-card-actions>
            </VCard>
          </VDialog>
        </div>
      </div>
    </div>
    <div class="unconfirmed-item__employee">
      <AvatarsGroup :items="prepAvatars"></AvatarsGroup>
    </div>
  </div>
</template>
<script lang="ts" setup>
import { computed, ref } from "vue";
import { Employee } from "../types/employee.type";
import AvatarsGroup from "./avatars-group.vue";
import axios from "axios";
import {
  confirmReservation,
  getCompanyEmployees,
  updateReservation,
} from "../services/api.service";
import { useCompanyStore } from "../stores/company.store";
import { Reservation } from "../types/reservation.type";
import { useI18n } from "vue-i18n";
import moment from "moment";

interface Props {
  title: string;
  description: string;
  employees?: Employee[];
  reservation: Reservation;
}

const { t } = useI18n();

const props = withDefaults(defineProps<Props>(), {
  title: "",
});

const emit = defineEmits<{
  (e: "updateReservation", value: Reservation): void;
  (e: "confirmReservation", value: Reservation): void;
}>();

const employeeLoading = ref(false);
const selectedEmployee = ref<number[]>([]);
const employeeErrorMessage = ref("");
const dialog = ref(false);
const companyStore = useCompanyStore();

const proposeDialog = ref(false);
const proposeLoading = ref(false);
const proposeErrorMessage = ref("");
const proposedStartDate = ref("");
const proposedEndDate = ref("");

const prepAvatars = computed<
  {
    alt: string;
    tooltip: string;
    img: string;
  }[]
>(() => {
  if (props.employees && props.employees.length) {
    const avatars = props.employees?.map((employee) => {
      return {
        alt: employee.name || "Unknown",
        tooltip: employee.name || "Unknown",
        img: (employee.avatar as string) || "",
      };
    });

    return avatars;
  }

  return [
    {
      alt: t("not_selected_employee"),
      tooltip: t("not_selected_employee"),
      img: "",
    },
  ];
});

const onChangeEmployeeDialog = async (opened: boolean) => {
  const filteredEmployees = props.reservation.employees.filter(
    (employee) => employee.id
  );
  selectedEmployee.value =
    filteredEmployees.map((employee) => employee.id!) || [];
  if (opened && !companyStore.employees.length) {
    employeeLoading.value = true;
    try {
      const employees = await getCompanyEmployees(
        companyStore.currentCompany?.id!
      );

      companyStore.employees = employees;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        employeeErrorMessage.value = `${error.message}`;
      } else {
        employeeErrorMessage.value = "Error";
        console.log(error);
      }
    }

    employeeLoading.value = false;
  }
};

const closeDialog = async (shouldSave: boolean) => {
  if (shouldSave) {
    try {
      const updatedReservation = await updateReservation({
        id: props.reservation.id,
        employee_ids: selectedEmployee.value,
      });

      emit("updateReservation", {
        ...props.reservation,
        employees: updatedReservation.employees,
      });
    } catch (error) {
      if (axios.isAxiosError(error)) {
        employeeErrorMessage.value = error.message;
      } else {
        employeeErrorMessage.value = "Error";
        console.log(error);
      }
    }
  }
  selectedEmployee.value = [];
  dialog.value = false;
};

// Prefills the new-time fields from the reservation's current start/end
// (converted to the browser's local time, matching what a datetime-local
// input expects) each time the dialog opens, so the owner is adjusting an
// existing time rather than starting from a blank slate.
const onProposeTimeDialog = (opened: boolean) => {
  if (!opened) return;
  proposeErrorMessage.value = "";
  proposedStartDate.value = moment(props.reservation.start_date).format(
    "YYYY-MM-DDTHH:mm",
  );
  proposedEndDate.value = moment(props.reservation.end_date).format(
    "YYYY-MM-DDTHH:mm",
  );
};

const closeProposeDialog = async (shouldSave: boolean) => {
  if (shouldSave) {
    if (
      !proposedStartDate.value ||
      !proposedEndDate.value ||
      proposedEndDate.value <= proposedStartDate.value
    ) {
      proposeErrorMessage.value = t("end_after_start");
      return;
    }

    proposeLoading.value = true;
    proposeErrorMessage.value = "";
    try {
      const updatedReservation = await updateReservation({
        id: props.reservation.id,
        start_date: moment(proposedStartDate.value).toISOString(),
        end_date: moment(proposedEndDate.value).toISOString(),
      });

      emit("updateReservation", updatedReservation);
      proposeDialog.value = false;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        proposeErrorMessage.value = error.message;
      } else {
        proposeErrorMessage.value = "Error";
        console.log(error);
      }
    }
    proposeLoading.value = false;
    return;
  }

  proposeDialog.value = false;
};

const onConfirmReservation = async () => {
  try {
    const reserv = await confirmReservation(props.reservation.id);

    emit("confirmReservation", reserv);
  } catch (error) {}
};
</script>

<style scoped lang="scss">
.unconfirmed-item {
  border-radius: 10px;
  background: #e7eaee;
  padding: 16px;
  display: flex;
  justify-content: space-between;
  &__content {
  }

  &__heading {
    h2 {
      color: #343d55;
      font-family: Inter;
      font-size: 14px;
      font-style: normal;
      font-weight: 600;
      line-height: 14px; /* 100% */
    }

    p {
      color: #353c4b;
      font-family: Inter;
      font-size: 12px;
      font-style: normal;
      font-weight: 400;
      line-height: 120%; /* 14.4px */
    }
  }

  &__employee {
    img {
      width: 24px;
      height: 24px;
    }
  }

  &__btns {
    display: flex;
    column-gap: 8px;

    button {
      border-radius: 4px;
      background: #fff;
      padding: 12px 24px;
      color: #343d55;
      text-align: center;
      font-family: "Inter";
      font-size: 12px;
      font-style: normal;
      font-weight: 500;
      line-height: 12px; /* 100% */
    }
  }

  button.unconfirmed-item__change {
    background-color: #4c4e51;
    color: #fff;
  }

  button.unconfirmed-item__propose {
    border: 1px solid #4c4e51;
    color: #4c4e51;
  }

  &__propose-hint {
    color: #696e7c;
    font-size: 12px;
    margin-bottom: 12px;
  }
}
</style>
